// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import { ReactSVG } from "react-svg";
import { ColumnsType } from "antd/es/table";
import { Link, useParams } from "react-router-dom";

// Local Imports
import CustomTable from "../../components/custom-table/custom-table";
import Heading from "../../components/heading/heading.component";
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import LockImage from "../../assets/lock.svg";
import KycVersionService from "./kyc-version.service";
import KycVersion from "../../models/kyc-version.model";
import ReactCountryFlag from "react-country-flag";
import clsx from "clsx";

const KycVersions: React.FC = (): ReactElement => {
  const kycVersionService = new KycVersionService();
  const { accountId }: { [key: string]: string | undefined } = useParams();
  const [kycVersionsData, setKycVersionsData] = useState<KycVersion>();

  useEffect(() => {
    fetchKycVersionsData();
  }, []);

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      sorter: (a, b) => a.id - b.id,
      fixed: "left",
      render: (_, __, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Full name",
      dataIndex: "nameEn",
      key: "nameEn",
      sorter: (a, b) => a.nameEn.length - b.nameEn.length,
      fixed: "left",
    },
    {
      title: "Account type",
      dataIndex: "accountType",
      key: "accountType",
      sorter: (a, b) => a.accountType.length - b.accountType.length,
    },
    {
      title: "Nationality",
      dataIndex: "nationality",
      key: "nationality",
      sorter: (a, b) =>
        a.nationality.nationalityEn.length - b.nationality.nationalityEn.length,
      render: (_, record) => (
        <div className="flex align-center">
          <ReactCountryFlag
            style={{
              fontSize: "24px",
              marginInlineEnd: "8px",
            }}
            countryCode={record?.nationality?.countryCode}
            svg
          />
          <span>{record?.nationality?.nationalityEn}</span>
        </div>
      ),
    },
    {
      title: "Version",
      dataIndex: "version",
      key: "version",
      sorter: (a, b) => a.account.length - b.account.length,
      render: (text, record) => (
        <div className="flex flex-col mb-2">
          <Link to={`/customers/change-list/${accountId}/${record?.id}`}>
            Version {text}{" "}
          </Link>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      sorter: (a, b) => a.status.length - b.status.length,
      filters: [
        {
          text: "Active",
          value: "active",
        },
        {
          text: "Inactive",
          value: "inactive",
        },
      ],
      render: (text) => (
        <span
          className={clsx(
            "rounded-md text-sm w-[80px] flex items-center justify-center h-[35px]",
            !text ? "bg-red-200 text-white" : "bg-gray-600 text-green-500"
          )}
        >
          {text ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Approved by",
      dataIndex: "lastLogin",
      key: "lastLogin",
      sorter: (a, b) => a.lastLogin.length - b.lastLogin.length,
      render: (_, record) =>
        record?.kycReview?.map((review: any, index: number) => {
          return (
            <span>
              {review?.feedback[0]?.reviewedBy?.name} (
              {review?.feedback[0]?.group}){" "}
              {index+1 !== record?.kycReview?.length ? ", " : ""}
            </span>
          );
        }),
    },
    {
      title: "Actions",
      key: "actions",
      width: "150px",
      render: () => (
        <div className="actions flex items-center gap-3 w-16">
          <ReactSVG src={LockImage} />
          <ReactSVG className="delete-icon" src={"/images/delete.svg"} />
        </div>
      ),
    },
  ];

  const fetchKycVersionsData = async (): Promise<void> => {
    const data = await kycVersionService.getKycVersionsData(accountId);

    if (data !== undefined) {
      setKycVersionsData(data);
    }
  };

  return (
    <div className="flex flex-col">
      <Heading
        text="KYC versions"
        size={4}
        className="text-lg font-medium mb-5 text-black-500"
      />
      <BreadcrumbComponent />
      <div className="mt-5 border-t-[1px] border-lightGray-300 rounded-lg">
        <CustomTable columns={columns} data={kycVersionsData} />
      </div>
    </div>
  );
};

export default KycVersions;
